import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Shield, Award, Clock, Users, CheckCircle2, ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    { icon: Shield, title: 'Quality First', desc: 'We never compromise on quality or safety standards' },
    { icon: Award, title: 'Expert Team', desc: 'F-Gas qualified engineers with years of experience' },
    { icon: Clock, title: 'Reliability', desc: 'Fast response times and dependable service' },
    { icon: Users, title: 'Customer Focus', desc: 'Your satisfaction is our top priority' },
  ];

  const certifications = [
    'F-Gas Compliant',
    'Gas Safe Registered',
    'NICEIC Approved',
    'TrustMark Certified',
    'Which? Trusted Trader',
    'Fully Insured',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Deep Control HVAC"
        description="Deep Control are F-Gas certified, REFCOM compliant HVAC specialists installing air conditioning across London. Fully insured, trusted by homeowners and businesses."
        path="/about"
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.70)), url('/images/hero-services.jpg')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-block text-xs bg-white/10 px-4 py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/30 tracking-wider mb-6">
            ABOUT US
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            London's Trusted <span className="text-[#FF8C00]">HVAC</span> Specialists
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
            Deep Control has been providing premium air conditioning and HVAC solutions across London for over a decade.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">OUR STORY</p>
              <h2 className="text-4xl font-bold mb-6">Quality, Reliability, Performance.</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Deep Control was founded with a simple mission: to provide London homes and businesses with the highest quality air conditioning and HVAC services.
                </p>
                <p>
                  Over the years, we've built a reputation for excellence through our commitment to quality workmanship, transparent pricing, and exceptional customer service.
                </p>
                <p>
                  Today, we're proud to serve thousands of satisfied customers across London and surrounding areas, from domestic installations to large-scale commercial projects.
                </p>
                <p>
                  Our team of F-Gas qualified engineers brings decades of combined experience, ensuring every installation, repair, and maintenance job is completed to the highest standards.
                </p>
              </div>
            </div>
            <div 
              className="h-[500px] rounded-2xl bg-cover bg-center"
              style={{
                backgroundImage: `url('/images/hero-about-secondary.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">OUR VALUES</p>
            <h2 className="text-4xl md:text-5xl font-bold">What We Stand For</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="bg-[#0A0E14] border border-white/5 rounded-xl p-8 text-center hover:border-[#FF8C00]/50 transition-all group">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF8C00]/10 flex items-center justify-center group-hover:bg-[#FF8C00]/20 transition-colors">
                    <Icon size={32} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-lg font-bold mb-3">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">CERTIFICATIONS</p>
            <h2 className="text-4xl md:text-5xl font-bold">Fully Accredited & Insured</h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {certifications.map((cert, idx) => (
              <div key={idx} className="bg-[#121820] border border-white/5 rounded-xl p-6 text-center hover:border-[#FF8C00]/50 transition-all">
                <CheckCircle2 size={32} className="text-[#FF8C00] mx-auto mb-3" />
                <p className="text-sm font-semibold">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to work with us?</h2>
          <p className="text-gray-400 text-lg mb-8">Get in touch with our team today.</p>
          <Link to="/contact">
            <Button className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold px-10 py-6 rounded-md text-base" data-testid="about-cta-contact">
              Contact Us Today <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
